import React from "react";
import {
  Box,
  Stack,
  Avatar,
  Typography,
  Divider,
  TextField,
  InputAdornment,
  Autocomplete,
  Checkbox,
  alpha,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export const ModernInputWrapper = ({ children, error, hasIcon }) => (
  <Box
    sx={{
      "& .MuiOutlinedInput-root": {
        borderRadius: "10px",
        backgroundColor: error ? "#fff5f5" : "#fff",
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        "& fieldset": { borderColor: error ? "#ffcdd2" : "#e0e0e0" },
        "&:hover": {
          backgroundColor: "#fafafa",
          "& fieldset": { borderColor: error ? "#ef5350" : "#bdbdbd" },
        },
        "&.Mui-focused": {
          backgroundColor: "#fff",
          boxShadow: `0 4px 12px ${alpha(error ? "#d32f2f" : "#1976d2", 0.15)}`,
          "& fieldset": {
            borderWidth: "1px",
            borderColor: error ? "#d32f2f" : "primary.main",
          },
        },
        "&.Mui-disabled": { backgroundColor: "#f5f5f5" },
      },
      "& .MuiInputLabel-root": {
        fontSize: "0.85rem",
        transform: hasIcon
          ? "translate(40px, 10px) scale(1)"
          : "translate(14px, 10px) scale(1)",
        "&.Mui-focused, &.MuiFormLabel-filled": {
          transform: "translate(14px, -9px) scale(0.75)",
        },
      },
      "& .MuiInputBase-input": {
        fontSize: "0.9rem",
        padding: "8.5px 14px",
      },
    }}
  >
    {children}
  </Box>
);

export const ModernCardHeader = ({ title, iconNode, color, count }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    sx={{ pb: 2, mb: 3, borderBottom: "1px solid rgba(0,0,0,0.06)" }}
  >
    <Stack direction="row" alignItems="center" spacing={2}>
      <Avatar
        variant="rounded"
        sx={{
          bgcolor: alpha(color, 0.1),
          color: color,
          width: 36,
          height: 36,
        }}
      >
        {iconNode && React.cloneElement(iconNode, { fontSize: "small" })}
      </Avatar>
      <Box>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 800,
            color: "text.primary",
            letterSpacing: "0.5px",
            textTransform: "uppercase",
            fontSize: "0.80rem",
          }}
        >
          {title}
        </Typography>
        {count !== undefined && (
          <Typography variant="caption" color="text.secondary">
            Toplam {count} Kayıt
          </Typography>
        )}
      </Box>
    </Stack>
  </Stack>
);

export const SubSectionHeader = ({ title, iconNode, color }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.5}
    sx={{ mb: 2, mt: 3 }}
  >
    <Box
      sx={{
        display: "flex",
        p: 0.8,
        borderRadius: "8px",
        bgcolor: alpha(color, 0.1),
        color: color,
      }}
    >
      {iconNode && React.cloneElement(iconNode, { fontSize: "small" })}
    </Box>
    <Typography
      variant="caption"
      sx={{
        fontWeight: 800,
        color: "text.secondary",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
      }}
    >
      {title}
    </Typography>
    <Divider sx={{ flexGrow: 1, borderColor: alpha(color, 0.2) }} />
  </Stack>
);

export const RenderInput = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  iconNode,
  readOnly = false,
  disabled = false,
  multiline = false,
  rows = 1,
  placeholder,
  suffix,
  error,
  onBlur,
}) => (
  <ModernInputWrapper hasIcon={!!iconNode} error={error}>
    <TextField
      fullWidth
      label={label}
      name={name}
      type={type}
      value={value === 0 ? "0" : value || ""}
      onChange={(e) => onChange(name, e.target.value)}
      onBlur={onBlur}
      variant="outlined"
      placeholder={placeholder}
      multiline={multiline}
      rows={multiline ? (rows > 1 ? rows : 3) : 1}
      disabled={disabled}
      error={error}
      InputLabelProps={type === "date" ? { shrink: true } : {}}
      InputProps={{
        readOnly: readOnly,
        startAdornment:
          iconNode && !multiline ? (
            <InputAdornment
              position="start"
              sx={{ color: "action.active", mr: 0.5 }}
            >
              {iconNode}
            </InputAdornment>
          ) : null,
        endAdornment: suffix ? (
          <InputAdornment position="end">
            <Typography variant="caption" color="text.secondary">
              {suffix}
            </Typography>
          </InputAdornment>
        ) : null,
      }}
      sx={
        readOnly || disabled
          ? { "& .MuiOutlinedInput-root": { bgcolor: "#f5f5f5" } }
          : {}
      }
    />
  </ModernInputWrapper>
);

export const RenderSelect = ({
  label,
  name,
  value,
  options = [],
  iconNode,
  onChange,
  disabled,
}) => {
  const safeValue = value === null || value === undefined ? "" : String(value);
  const safeOptions = Array.isArray(options)
    ? options.filter((o) => o !== null && o !== undefined)
    : [];

  return (
    <ModernInputWrapper hasIcon={!!iconNode}>
      <Autocomplete
        freeSolo
        disabled={disabled}
        options={safeOptions}
        value={safeValue}
        inputValue={safeValue}
        getOptionLabel={(option) => {
          if (typeof option === "object") return option.label || option.code;
          return option === null || option === undefined ? "" : String(option);
        }}
        isOptionEqualToValue={(option, selectedValue) => {
          const optVal =
            typeof option === "object" ? option.code : String(option ?? "");
          const valVal =
            typeof selectedValue === "object"
              ? selectedValue.code
              : String(selectedValue ?? "");
          return optVal === valVal;
        }}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === "input" || reason === "clear") {
            onChange(name, newInputValue ?? "");
          }
        }}
        onChange={(event, newValue) => {
          if (typeof newValue === "object" && newValue !== null) {
            onChange(name, newValue.code);
          } else {
            onChange(
              name,
              newValue === null || newValue === undefined
                ? ""
                : String(newValue),
            );
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            variant="outlined"
            disabled={disabled}
            InputProps={{
              ...params.InputProps,
              startAdornment: iconNode ? (
                <InputAdornment
                  position="start"
                  sx={{ color: "action.active", mr: 0.5 }}
                >
                  {iconNode}
                </InputAdornment>
              ) : null,
            }}
          />
        )}
      />
    </ModernInputWrapper>
  );
};

export const RenderMultiSelect = ({
  label,
  name,
  value,
  options = [],
  onChange,
}) => {
  const safeValue = Array.isArray(value)
    ? value.filter((item) => item !== null && item !== undefined)
    : [];
  const safeOptions = Array.isArray(options)
    ? options.filter((option) => option !== null && option !== undefined)
    : [];

  return (
    <ModernInputWrapper>
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={safeOptions}
        value={safeValue}
        getOptionLabel={(option) =>
          option === null || option === undefined ? "" : String(option)
        }
        onChange={(event, newValue) => {
          onChange(name, Array.isArray(newValue) ? newValue : []);
        }}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              sx={{ mr: 1 }}
              checked={selected}
            />
            {String(option ?? "")}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            variant="outlined"
            placeholder="Seçiniz..."
          />
        )}
      />
    </ModernInputWrapper>
  );
};
